import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { ClassicItemIcon } from '@/components/GameIcon';
import { cn } from '@/lib/utils';

export interface ClassicRewardItem {
  id: string;
  name: string;
  maxAmount: number;
  itemCategory?: number;
}
type Category = 'currency' | 'consumable' | 'material' | 'costume';
const categories = [
  { id: 'all', label: 'All' },
  { id: 'currency', label: 'Currency' },
  { id: 'consumable', label: 'Consumable' },
  { id: 'material', label: 'Material' },
  { id: 'costume', label: 'Costume' },
] as const;
export function classicRewardCategory(item: Pick<ClassicRewardItem, 'id' | 'itemCategory'>): Category {
  if (item.id === 'gold' || item.id === 'ruby') return 'currency';
  if (item.id.startsWith('costume_')) return 'costume';
  return item.itemCategory === 2 ? 'consumable' : 'material';
}
export function ClassicCategoryBadge({ item }: { item: Pick<ClassicRewardItem, 'id' | 'itemCategory'> }) {
  const category = classicRewardCategory(item);
  const colors = { currency: 'border-amber-500/30 bg-amber-500/10 text-amber-200', consumable: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200', material: 'border-sky-500/30 bg-sky-500/10 text-sky-200', costume: 'border-violet-500/30 bg-violet-500/10 text-violet-200' };
  return <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] capitalize', colors[category])}>{category}</span>;
}

/** EC2's input-triggered catalog selector, using Classic IDs, categories and art. */
export function ClassicItemPicker({ value, items, onChange, disabled = false, loading = false }: {
  value: string;
  items: ClassicRewardItem[];
  onChange: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [above, setAbove] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<(typeof categories)[number]['id']>('all');
  const [active, setActive] = useState(0);
  const wrapper = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = items.find(item => item.id === value);
  useEffect(() => {
    const closeOutside = (event: MouseEvent) => { if (!wrapper.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, []);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const { matches, counts } = useMemo(() => {
    const counts = { all: 0, currency: 0, consumable: 0, material: 0, costume: 0 };
    const matches = items.filter(item => {
      if (!`${item.name} ${item.id}`.toLowerCase().includes(query.trim().toLowerCase())) return false;
      const kind = classicRewardCategory(item);
      counts.all++; counts[kind]++;
      return category === 'all' || category === kind;
    });
    return { matches, counts };
  }, [items, query, category]);
  const activeIndex = Math.min(active, Math.max(0, matches.length - 1));
  const visible = open && !disabled;
  useEffect(() => {
    if (visible) document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, visible, listId]);
  function pick(item: ClassicRewardItem) {
    onChange(item.id); setQuery(''); setOpen(false); setActive(0);
  }
  function reveal() { if (!disabled && !open) { const rect = wrapper.current?.getBoundingClientRect(); setAbove(!!rect && window.innerHeight - rect.bottom < 320 && rect.top > window.innerHeight - rect.bottom); setQuery(''); setActive(0); setOpen(true); } }
  return <div ref={wrapper} className="relative min-w-0 basis-full flex-1 sm:basis-0" onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); setOpen(false); } }} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <div className="relative">
      {selected && !visible && <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"><ClassicItemIcon id={selected.id.startsWith('costume_') ? 'costume' : selected.id} className="h-7 w-7" /></span>}
      <Input role="combobox" aria-label="Reward item" aria-expanded={visible} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={visible && matches.length ? `${listId}-${activeIndex}` : undefined}
        disabled={disabled} value={visible ? query : selected?.name ?? ''} placeholder={loading ? 'Loading Classic items…' : 'Search items'}
        className={cn(selected && !visible && 'pl-11 pr-28')} onFocus={reveal} onClick={reveal}
        onChange={event => { setOpen(true); setQuery(event.target.value); setActive(0); }}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); setOpen(false); return; }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!visible) { reveal(); return; }
            setActive(Math.max(0, Math.min(matches.length - 1, activeIndex + (event.key === 'ArrowDown' ? 1 : -1))));
          }
          if (event.key === 'Enter' && visible) { event.preventDefault(); if (matches[activeIndex]) pick(matches[activeIndex]); }
        }} />
      {selected && !visible && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"><ClassicCategoryBadge item={selected} /></span>}
    </div>
    {visible && <div className={cn("absolute z-50 max-h-80 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg", above ? "bottom-full mb-1" : "mt-1")}>
      <div className="sticky top-0 z-10 flex flex-wrap gap-1 border-b border-border bg-card p-2" role="group" aria-label="Item categories">
        {categories.map(tab => <button key={tab.id} type="button" aria-pressed={category === tab.id} onMouseDown={event => event.preventDefault()}
          onClick={() => { setCategory(tab.id); setActive(0); }} className={cn('rounded-full border px-2 py-0.5 text-[10px] transition-colors', category === tab.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent')}>
          {tab.label}<span className="ml-1 font-mono opacity-70">{counts[tab.id]}</span>
        </button>)}
      </div>
      <div id={listId} role="listbox" aria-label="Classic items">
        {matches.map((item, index) => <button key={item.id} id={`${listId}-${index}`} role="option" aria-selected={item.id === value} type="button" onMouseDown={event => event.preventDefault()} onClick={() => pick(item)}
          className={cn('flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent', index === activeIndex && 'bg-accent/60')}>
          <ClassicItemIcon id={item.id.startsWith('costume_') ? 'costume' : item.id} className="h-8 w-8 shrink-0" />
          <span className="min-w-0 flex-1"><span className="block truncate">{item.name}</span><span className="block truncate font-mono text-[10px] text-muted-foreground">{item.id}</span></span>
          <ClassicCategoryBadge item={item} />
        </button>)}
      </div>
      {!matches.length && <p role="status" className="px-3 py-3 text-xs text-muted-foreground">{loading ? 'Loading Classic items…' : 'No matches.'}</p>}
    </div>}
  </div>;
}
